export type ConsentChoice = "granted" | "necessary_only";

export const CONSENT_BRIDGE_PARAM_KEY = "consent";
export const CONSENT_STORAGE_KEY = "qrhub_consent_v1";
export const CONSENT_COOKIE_KEY = "qrhub_consent_v1";
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
export const CONSENT_COOKIE_DOMAIN = ".qrhub.io";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function isConsentChoice(value: unknown): value is ConsentChoice {
  return value === "granted" || value === "necessary_only";
}

function resolveCookieDomain(cookieDomain?: string): string | undefined {
  if (!cookieDomain) return undefined;
  if (!isBrowser()) return cookieDomain;
  // Domain=.qrhub.io only works on qrhub.io hosts; skip on localhost/dev.
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    !window.location.hostname.endsWith("qrhub.io")
  ) {
    return undefined;
  }
  return cookieDomain;
}

export function setDefaultConsent(): void {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push([
    "consent",
    "default",
    {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    },
  ]);
}

export function updateConsent(choice: ConsentChoice): void {
  if (!isBrowser()) return;
  const value = choice === "granted" ? "granted" : "denied";
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push([
    "consent",
    "update",
    {
      analytics_storage: value,
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value,
    },
  ]);
}

export function getStoredConsentChoice(): ConsentChoice | null {
  if (!isBrowser()) return null;

  try {
    const fromLocal = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (isConsentChoice(fromLocal)) return fromLocal;
  } catch {
    // Ignore blocked storage
  }

  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${CONSENT_COOKIE_KEY}=`));
    const value = match?.substring(CONSENT_COOKIE_KEY.length + 1);
    return isConsentChoice(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeConsentChoice(
  choice: ConsentChoice,
  options?: { cookieDomain?: string },
): void {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Ignore blocked storage
  }

  try {
    const domain = resolveCookieDomain(options?.cookieDomain);
    const domainPart = domain ? `; Domain=${domain}` : "";
    document.cookie = `${CONSENT_COOKIE_KEY}=${choice}; Path=/; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${domainPart}`;
  } catch {
    // Ignore cookie write failures
  }
}

export function applyStoredConsentOnLoad(): ConsentChoice | null {
  const choice = getStoredConsentChoice();
  if (choice) updateConsent(choice);
  return choice;
}

export function getConsentBridgeParam(): Record<string, string> {
  const choice = getStoredConsentChoice();
  return choice ? { [CONSENT_BRIDGE_PARAM_KEY]: choice } : {};
}

export function extractBridgedConsentFromSearch(
  search: string,
): ConsentChoice | null {
  try {
    const params = new URLSearchParams(
      search.startsWith("?") ? search : `?${search}`,
    );
    const value = params.get(CONSENT_BRIDGE_PARAM_KEY);
    return isConsentChoice(value) ? value : null;
  } catch {
    return null;
  }
}
